import { $t } from '@/i18n';
import COS from '@tencent/innercos-js-sdk';
import getXyHeaders from '@utils/getXyHeaders';
import fetch from '@utils/fetch';
import { addPathPrefix } from '@utils/path';
import pick from 'lodash/pick';

const uploadPathPrefix = window.GLOBAL_INFO.uploadPathPrefix || '/wupload/xy/';
const imageUploadPathPrefix = window.GLOBAL_INFO.imageUploadPathPrefix || '/wupload/xy/';

// 内部cos和外部cos从host获取地区有一点差异，内部cos在第一个，外部cos在第二个数组项
// 内部cos的host：sz.gfp.tencent-cloud.com
// 外部cos的host：cos.ap-guangzhou.myqcloud.com
const getRegion = (host) => {
  const innerCos = !!host.match(/tencent-cloud\.com$/);
  const hostArr = host.split('.');
  if (innerCos) {
    return hostArr[0];
  }
  return hostArr[1];
};

const isInnerCos = host => !!host.match(/tencent-cloud\.com$/);

class Uploader {
  cosId = 'default';
  cosInfo = {};
  cosInstance;

  /**
   *  给没有配置cos，需要直接上传到入口配置的fileUploader插件
   */
  customUpload({ file, filePath, projectId, timeout = 10000, type = 'image' }) {
    const defaultPrefix = type === 'image' ? imageUploadPathPrefix : uploadPathPrefix;
    const filePathArr = [`${defaultPrefix}${projectId}`, ...filePath.split('/')];
    const pathPrefix = filePathArr.slice(0, filePathArr.length - 1).join('/');
    const filename = encodeURIComponent(filePathArr[filePathArr.length - 1]);
    return this.uploadFile({ file, pathPrefix, filename, projectId, timeout });
  }

  /**
   * cos实例初始化
   * @param cosId 默认为wuji,使用调用无极2.0server配置的无极cos; 如果指定项目的cosid，则会使用业务方自定义好的cos配置上传
   */
  async init({ cosId, projectId, fileType } = {}, timeout) {
    this.cosId = cosId || this.cosId;
    const res = await fetch(`/api/xy/basic/cos/${this.cosId}?projectid=${projectId}`, {}, timeout);
    if (!res?.data) throw new Error($t('k_85a2ac6d'));
    this.cosInfo = res.data;
    const { appid, bucket, domain } = this.cosInfo;
    this.cosInstance = new COS({
      CompatibilityMode: true,
      UploadCheckContentMd5: true,
      Domain: `${bucket}-${appid}.${domain}`,
      Timeout: timeout,
      getAuthorization: async (options, callback) => {
        try {
          const data = pick(options, ['Method', 'method', 'Headers',
            'headers', 'Host', 'host', 'Pathname', 'pathname',
            'Parameters', 'parameters', 'Query']);
          const res = await fetch(`/api/xy/cos/auth/${this.cosId}?projectid=${projectId}`, {
            method: 'POST',
            headers: {
              ...getXyHeaders(),
            },
            body: JSON.stringify({
              options: {
                ...data,
                Query: {
                  ...(data.Query || {}),
                  fileType,
                },
              },
            }),
          }, timeout);
          if (typeof res.data === 'string') {
            callback({
              Authorization: res.data,
            });
          } else {
            callback({
              Authorization: res.data.Authorization,
              SecurityToken: res.data.SecurityToken,
            });
          }
        } catch (err) {
          callback(undefined, err.message);
        }
      },
    });
  }

  /**
   * 上传文件方法（需根据projectId等信息进行鉴权）, 直接通过 cos 上传
   * @param {any} file 文件对象
   * @param {any} filePath 文件上传路径
   * @param {any} onProgress 上传进度处理函数
   * @returns {any}
   */
  upload({ file, filePath, onProgress, projectId, cosId, timeout = 10000 }) {
    if (!cosId) {
      return this.customUpload({ file, filePath, projectId, timeout });
    }
    return new Promise(async (resolve, reject) => {
      // 如果之前没有初始过cos实例, 先初始化一个
      try {
        await this.init({ cosId, projectId, fileType: file.type }, timeout);
      } catch (err) {
        reject($t('k_eec9fca9'));
      }
      const { bucket, publicDomain, domain, appid, pathPrefix, auth } = this.cosInfo;
      const uri = pathPrefix
        ? `${pathPrefix.replace(/\/$/, '')}/${filePath.replace(/^\//, '')}`
        : `${uploadPathPrefix}${projectId}/${filePath.replace(/^\//, '')}`;
      const options = {
        Bucket: `${bucket}-${appid}`,
        Region: getRegion(domain),
        Key: uri,
        Body: file, // 上传文件对象
        isInnerCos: isInnerCos(domain),
        onProgress(progressData) {
          const { percent } = progressData;
          onProgress(Math.round(percent * 100));
        },
      };
      const handler = (err, data) => {
        if (err) {
          reject(err);
          return;
        }
        if (data.statusCode === 200) {
          let path = `https://${data.Location}`;
          if (auth === 1) {
            path = addPathPrefix(`/api/xy/file/cosfile?cosid=${this.cosId}&key=${uri}`);
          } else if (publicDomain) {
            path = path.replace(/(^https:\/\/)([.a-zA-Z0-9_-]+)(\/.*$)/, `$1${publicDomain}$3`);
          }
          resolve(path);
        }
      };
      // 大于100MB走分片上传，否则走简单上传
      if (file.size <= 1024 * 1024 * 100) {
        this.cosInstance.putObject(options, handler);
      } else {
        this.cosInstance.sliceUploadFile({
          ...options,
          SliceSize: 1024 * 1024 * 30, // 每片分片大小为30M
        }, handler);
      }
    });
  }

  /**
   * 上传 xy 文件方法（需根据projectId等信息进行鉴权）
   * @param {any} file 文件对象
   * @param {any} path 文件上传路径
   * @param {any} keepFilename 上传文件是否保持原名
   * @param {any} onProgress 上传进度处理函数
   * @returns {any}
   */
  uploadXyFile({ file, filePath, onProgress }, { projectId, cosId, timeout }) {
    return this.uploadThroughXy({ file, filePath, onProgress, url: '/api/xy/file/uploadFile' }, {
      projectId,
      cosId,
      timeout,
      type: 'file',
    });
  }

  /**
   * 上传图片方法（需根据projectId等信息进行鉴权）
   * @param {any} file 文件对象
   * @param {any} path 文件上传路径
   * @param {any} keepFilename 上传文件是否保持原名
   * @param {any} onProgress 上传进度处理函数
   * @returns {any}
   */
  uploadImage({ file, filePath, onProgress }, { projectId, cosId, timeout }) {
    return this.uploadThroughXy({ file, filePath, onProgress, url: '/api/xy/file/uploadImage' }, {
      projectId,
      cosId,
      timeout,
      type: 'image',
    });
  }

  /**
   * 通过后台上传
   * @param {any} file 文件对象
   * @param {any} path 文件上传路径
   * @param {any} keepFilename 上传文件是否保持原名
   * @param {string} url 上传接口
   * @param {any} onProgress 上传进度处理函数
   * @returns {any}
   */
  uploadThroughXy({ file, filePath, onProgress, url }, { projectId, cosId, timeout, type }) {
    if (!cosId) {
      return this.customUpload({ file, filePath, projectId, timeout, type });
    }
    return new Promise(async (resolve, reject) => {
      try {
        await this.init({ cosId, projectId }, timeout);
        if (this.cosInfo.directUpload) {
          return this.upload({ file, filePath, onProgress, projectId, cosId })
            .then(resolve)
            .catch(reject);
        }
        const formData = new FormData();
        formData.append('file', file);
        const xhr = new XMLHttpRequest();
        if (onProgress) {
          xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable) {
              const progress = Math.round((event.loaded * 100) / event.total);
              onProgress(progress);
            }
          }, false);
        }

        xhr.addEventListener('load', () => {
          if (xhr.readyState === 4 && xhr.status === 200) {
            const res = JSON.parse(xhr.response);
            if (res.code === 200) {
              resolve(res.data);
            } else {
              reject(res);
            }
          } else {
            const res = JSON.parse(xhr.response);
            reject({ code: xhr.status, message: res.message || res.error });
          }
        }, false);
        xhr.addEventListener('error', (event) => {
          reject(event);
        }, false);
        const key = `${type === 'image' ? imageUploadPathPrefix : uploadPathPrefix}${projectId}/${filePath.replace(/^\//, '')}`;
        xhr.open('post', addPathPrefix(`${url}?projectid=${projectId}&cosid=${cosId}&key=${key}&filepath=${filePath}`), true);
        Object.entries(getXyHeaders()).forEach(([key, value]) => {
          xhr.setRequestHeader(key, value);
        });
        xhr.timeout = timeout || 10000;
        xhr.withCredentials = true;
        xhr.send(formData);
      } catch (e) {
        reject(e);
        console.error(e);
      }
    });
  }

  /**
   * 使用自定义的fileUploader上传文件
   * 该接口使用的是 fileUploader 配置的 cos
   **/
  async uploadFile({ file, pathPrefix = 'wuji_material', filename, projectId, timeout = 10000 }) {
    const formData = new FormData();
    formData.append('data', file);
    formData.append('pathPrefix', pathPrefix);
    formData.append('filename', filename);
    const result = await fetch(`/api/xy/file/upload?projectid=${projectId}`, {
      method: 'POST',
      headers: {
        ...getXyHeaders(),
      },
      body: formData,
      noContentType: false,
    }, timeout);
    return result.data.path;
  }

  cosInfoMap = {};

  async fetchAndCacheCosInfo(projectId) {
    if (this.cosInfoMap[projectId]) {
      return this.cosInfoMap[projectId];
    }
    const res = await fetch(`/api/xy/basic/cos?projectid=${projectId}`);
    this.cosInfoMap[projectId] = res.data;
    return this.cosInfoMap[projectId];
  }
}

const uploader = new Uploader();

export default uploader;
